/**
 * THE CAST LOADER — fetch, verify and instantiate one character at one tier.
 *
 * ── How this differs from `authoredMasterLoader.ts`, and why ────────────────
 *
 * The master loader loads ONE character and shares its geometry, material and
 * atlas across twenty-two players. That sharing IS the performance architecture
 * there, and it is exactly what a cast cannot do: thirty-six distinct people are
 * thirty-six geometries, thirty-six material sets and thirty-six texture sets by
 * definition. So this loader is keyed on `(characterId, lod)` and caches per
 * key, and the interesting question stops being "is it loaded" and becomes "how
 * much is loaded at once".
 *
 * Everything else is deliberately the same shape as the master loader, because
 * the failures are the same failures:
 *
 *   - loading is an EXPLICIT PHASE (`preloadCast`), never a promise hidden
 *     inside a synchronous build();
 *   - a model that does not satisfy the contract is REFUSED, not drawn — a rig
 *     4 mm off the bind pose does not look broken, it looks like slightly bad
 *     animation, which is the most expensive class of bug this project ships;
 *   - a refusal is recorded BY CHARACTER NAME so the screen can say whose model
 *     is missing rather than showing a stranger in his shirt.
 *
 * ── What is NOT verified here, and why ─────────────────────────────────────
 *
 * Byte hashes. `castManifest.ts` records them so a measurement can be traced to
 * specific bytes, but the cast is rebuilt daily by four agents in parallel and a
 * hash gate would be red every morning. The gate is the CONTRACT — 74 joints
 * rooted at `root`, 12 sockets, ≤4 influences, no clips in the mesh, feet on
 * y=0 — which is locked by `A1_A2_CONTRACT.md` and does not move when somebody
 * re-bakes a face. See `castContract.ts`.
 */
import {
  Texture,
  type AnimationClip,
  type Bone,
  type BufferGeometry,
  type Material,
  type Object3D,
  type Skeleton,
  type SkinnedMesh,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { findHandRig, type HandRig } from '../handAttach';
import {
  CAST_SOCKET_PREFIX,
  checkCastLod,
  refuses,
  type CastFault,
  type CastLodLevel,
  type CastLodMeasurement,
} from './castContract';
import {
  castTexturePlugin,
  dracoDecodeUsable,
  getDracoLoader,
  getKTX2Loader,
  isSharedTexture,
} from './castDecoders';
import { castCharacter, castLodAsset, type CastCharacter } from './castManifest';
import { AUTHORED_MASTER_HIPS_REST_Y, ROOT_MOTION_BONE } from './authoredMasterLoader';

/** One character at one tier, loaded and verified. */
export interface CastLod {
  readonly id: string;
  readonly lod: CastLodLevel;
  readonly url: string;
  /** The parsed scene, cloned per player. Owns geometry, materials and textures. */
  readonly template: Object3D;
  readonly triangles: number;
  readonly meshes: number;
  readonly materials: number;
  readonly textures: number;
  readonly boneOrder: readonly string[];
  readonly heightM: number;
  readonly soleM: number;
  /** Rest world height of `hips`, metres. See `restHipsY` on CastInstance. */
  readonly hipsRestY: number;
  /** Budget faults — drawn anyway, counted here. Contract faults never get this far. */
  readonly warnings: readonly CastFault[];
}

export class CastLoadError extends Error {
  constructor(
    readonly id: string,
    readonly lod: number,
    readonly faults: readonly CastFault[],
  ) {
    super(
      `cast character "${id}" LOD${lod} does not satisfy the A2 contract:\n` +
        faults.map((f) => `  - ${f.detail}`).join('\n'),
    );
    this.name = 'CastLoadError';
  }
}

const key = (id: string, lod: number): string => `${id}#${lod}`;
const loaded = new Map<string, CastLod>();
const inFlight = new Map<string, Promise<CastLod>>();
/** Why a character is not on the pitch, by id. Read by the banner. */
const refusals = new Map<string, string>();

export function castRefusals(): ReadonlyMap<string, string> {
  return refusals;
}

const canDecodeImages = (): boolean =>
  typeof createImageBitmap !== 'undefined' || typeof document !== 'undefined';

/**
 * Every map slot a cast material can carry. One list, read twice: once to
 * MEASURE a tier's texture set and once to decide what may be freed.
 */
const TEXTURE_SLOTS = [
  'map', 'normalMap', 'roughnessMap', 'metalnessMap', 'emissiveMap', 'aoMap', 'alphaMap',
] as const;

let warnedNoDraco = false;

/**
 * THE loader every cast GLB is parsed with — the optimised cast is Draco
 * geometry plus external KTX2 images, and neither decodes without being wired
 * up here.
 *
 * `decodeTextures` is the headless switch it has always been. It now also
 * covers the KTX2 path: Draco decode is geometry arithmetic and stays REAL in
 * Node (given a decoder — see `nodeDracoLoader.ts`), while Basis transcode
 * needs a GPU format chosen off a live `WebGLRenderer`, so texture decode is
 * what gets skipped and geometry never is. A contract test measures joints,
 * sockets and triangles; all three survive.
 *
 * Exported because it is worth testing directly: the plugin it installs claims
 * three's own `KHR_texture_basisu` slot (see `castDecoders.ts`), and that is
 * exactly the kind of arrangement that breaks silently on a three upgrade.
 */
export function castGltfLoader(decodeTextures: boolean): GLTFLoader {
  const loader = new GLTFLoader();
  loader.setDRACOLoader(getDracoLoader());
  if (decodeTextures) loader.setKTX2Loader(getKTX2Loader());
  if (!dracoDecodeUsable() && !warnedNoDraco) {
    warnedNoDraco = true;
    console.warn(
      '[cast] no Worker/createObjectURL in this environment — three’s DRACOLoader ' +
        'cannot decode here. A Draco-compressed cast GLB will fail to parse until an ' +
        'in-process decoder is installed with setDracoLoader() (see nodeDracoLoader.ts).',
    );
  }
  // Replaces three's built-in KHR_texture_basisu handler. Two jobs: skip image
  // decode entirely when there is no DOM (bare Node reaches for
  // `self`/`createImageBitmap` and throws before a joint is read — skeleton,
  // skinning and geometry are untouched by that), and otherwise route every
  // external image through the cross-file pool so 36 characters sharing one
  // KTX2 file cost one upload rather than 36.
  loader.register((parser) => castTexturePlugin(parser, { decode: decodeTextures }));
  return loader;
}

/** Measure a parsed cast scene the same way the freezer measures the file. */
function measure(scene: Object3D, animations: readonly AnimationClip[]): CastLodMeasurement {
  let meshes = 0;
  let primitives = 0;
  let triangles = 0;
  let maxInfluences = 0;
  let sockets = 0;
  let minY = Infinity;
  let maxY = -Infinity;
  const materials = new Set<Material>();
  const textures = new Set<Texture>();
  let bones = 0;
  let rootBone: string | null = null;
  let hipsRestY = 0;

  scene.traverse((o) => {
    if ((o as Object3D).name.startsWith(CAST_SOCKET_PREFIX)) sockets++;
    // THE HIP DATUM. The clips carry an ABSOLUTE hips translation written
    // against the master's rest hip height, so this character's own rest hip
    // height is the correction — see AUTHORED_MASTER_HIPS_REST_Y. Read here,
    // once per tier, off the same updated world matrices the bounds use.
    if ((o as Bone).isBone && o.name === ROOT_MOTION_BONE) {
      hipsRestY = o.matrixWorld.elements[13] as number;
    }
    const mesh = o as SkinnedMesh;
    if (!mesh.isMesh) return;
    meshes++;
    const geom = mesh.geometry as BufferGeometry;
    const groups = geom.groups?.length ?? 0;
    primitives += groups > 1 ? groups : 1;
    const index = geom.getIndex();
    const pos = geom.getAttribute('position');
    triangles += index ? index.count / 3 : pos ? pos.count / 3 : 0;
    const si = geom.getAttribute('skinIndex');
    if (si) maxInfluences = Math.max(maxInfluences, si.itemSize);
    for (const m of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
      if (!m) continue;
      materials.add(m);
      for (const slot of TEXTURE_SLOTS) {
        const t = (m as unknown as Record<string, Texture | null>)[slot];
        if (t) textures.add(t);
      }
    }
    geom.computeBoundingBox();
    const box = geom.boundingBox;
    if (box) {
      minY = Math.min(minY, box.min.y);
      maxY = Math.max(maxY, box.max.y);
    }
    if (mesh.isSkinnedMesh && mesh.skeleton && bones === 0) {
      bones = mesh.skeleton.bones.length;
      const root = mesh.skeleton.bones.find((b) => !(b.parent as Bone | null)?.isBone);
      rootBone = (root ?? mesh.skeleton.bones[0])?.name ?? null;
    }
  });

  return {
    meshes,
    primitives,
    materials: materials.size,
    textures: textures.size,
    triangles,
    bones,
    rootBone,
    sockets,
    maxInfluences,
    animations: animations.length,
    heightM: Number.isFinite(maxY) ? maxY - minY : 0,
    soleM: Number.isFinite(minY) ? minY : 0,
    hipsRestY,
  };
}

export interface CastLoadOptions {
  readonly fetchBytes?: (url: string) => Promise<ArrayBuffer>;
  readonly decodeTextures?: boolean;
}

async function loadOne(
  id: string,
  lod: CastLodLevel,
  opts: CastLoadOptions,
): Promise<CastLod> {
  const character = castCharacter(id);
  const asset = castLodAsset(id, lod);
  if (!character || !asset) {
    const why = character
      ? `no LOD${lod} in the manifest for "${id}" — the ladder ships ` +
        `${character.lods.map((l) => l.lod).join('/')}`
      : `"${id}" is not in the cast manifest. Rebuild it with ` +
        `\`node scripts/freeze-cast-manifest.mjs\` after the model lands.`;
    refusals.set(id, why);
    throw new CastLoadError(id, lod, [{ id, lod, kind: 'contract', detail: why }]);
  }

  const decodeTextures = opts.decodeTextures ?? canDecodeImages();
  const bytes = opts.fetchBytes
    ? await opts.fetchBytes(asset.url)
    : await (async () => {
        const res = await fetch(asset.url);
        if (!res.ok) {
          throw new CastLoadError(id, lod, [
            { id, lod, kind: 'contract', detail: `HTTP ${res.status} for ${asset.url} — not deployed` },
          ]);
        }
        return res.arrayBuffer();
      })();

  const gltf = await new Promise<{ scene: Object3D; animations: AnimationClip[] }>((ok, no) => {
    const dir = asset.url.slice(0, asset.url.lastIndexOf('/') + 1);
    castGltfLoader(decodeTextures).parse(bytes, dir, (g) => ok(g as never), no);
  });
  gltf.scene.updateMatrixWorld(true);

  const m = measure(gltf.scene, gltf.animations);
  const faults = checkCastLod(id, lod, m, character.declaredHeightM);
  if (refuses(faults)) {
    const err = new CastLoadError(id, lod, faults.filter((f) => f.kind === 'contract'));
    refusals.set(id, err.message);
    console.error('[cast]', err.message);
    throw err;
  }
  refusals.delete(id);

  const skinned: SkinnedMesh[] = [];
  gltf.scene.traverse((o) => {
    if ((o as SkinnedMesh).isSkinnedMesh) skinned.push(o as SkinnedMesh);
  });
  const boneOrder = skinned[0]?.skeleton?.bones.map((b) => b.name) ?? [];

  return {
    id,
    lod,
    url: asset.url,
    template: gltf.scene,
    triangles: m.triangles,
    meshes: m.meshes,
    materials: m.materials,
    textures: m.textures,
    boneOrder,
    heightM: m.heightM,
    soleM: m.soleM,
    hipsRestY: m.hipsRestY ?? AUTHORED_MASTER_HIPS_REST_Y,
    warnings: faults.filter((f) => f.kind === 'budget'),
  };
}

/**
 * Load one character at one tier. Idempotent and de-duplicated: a squad of
 * twenty-two racing on startup produces at most one fetch per (id, lod).
 */
export function loadCastLod(
  id: string,
  lod: CastLodLevel,
  opts: CastLoadOptions = {},
): Promise<CastLod> {
  const k = key(id, lod);
  const have = loaded.get(k);
  if (have) return Promise.resolve(have);
  const busy = inFlight.get(k);
  if (busy) return busy;
  const p = loadOne(id, lod, opts)
    .then((l) => {
      loaded.set(k, l);
      return l;
    })
    .finally(() => {
      inFlight.delete(k);
    });
  inFlight.set(k, p);
  return p;
}

export function castLodLoaded(id: string, lod: CastLodLevel): CastLod | undefined {
  return loaded.get(key(id, lod));
}

/**
 * Load a whole match's worth of characters.
 *
 * Sequential on purpose. Twenty-two parallel fetches of a 5 MB GLB saturate the
 * connection and the main thread decodes thirteen PNGs per model as they land —
 * a burst that stalls the first frames of a match by seconds. One at a time
 * keeps the page responsive and the ordering is what a caller asked for, so the
 * players who matter can be first in the list.
 */
export async function preloadCast(
  ids: readonly string[],
  lods: readonly CastLodLevel[],
  opts: CastLoadOptions & { onProgress?: (done: number, total: number, id: string) => void } = {},
): Promise<{ loaded: CastLod[]; failed: { id: string; lod: number; why: string }[] }> {
  const out: CastLod[] = [];
  const failed: { id: string; lod: number; why: string }[] = [];
  const total = ids.length * lods.length;
  let done = 0;
  for (const id of ids) {
    for (const lod of lods) {
      try {
        out.push(await loadCastLod(id, lod, opts));
      } catch (e) {
        failed.push({ id, lod, why: e instanceof Error ? e.message : String(e) });
      }
      opts.onProgress?.(++done, total, id);
    }
  }
  return { loaded: out, failed };
}

// ── instantiation ───────────────────────────────────────────────────────────

export interface CastInstance {
  readonly id: string;
  readonly lod: CastLodLevel;
  readonly root: Object3D;
  readonly meshes: SkinnedMesh[];
  readonly skeleton: Skeleton;
  readonly bones: Bone[];
  /**
   * This instance's hands, resolved once here. (§4.5)
   *
   * Held props are parented to these — see `render/handAttach.ts`. Resolved at
   * instantiation rather than at use because the answer cannot change and a
   * name search over 74 bones, per player, per frame, would be real cost for a
   * constant. `EMPTY_HAND_RIG` if this model's arms are named something the
   * matcher does not know, which degrades to "the ball stays where the sim put
   * it" rather than to a wrong attachment.
   */
  readonly hands: HandRig;
  readonly triangles: number;
  /** One draw call per primitive. The number a squad multiplies. */
  readonly drawCalls: number;
  /**
   * His rest hip height above his own soles, metres — the number that makes the
   * clip pack fit these legs. Handed to `PlayerAnimator`, which turns it into a
   * leg-length ratio and multiplies the hips' vertical channel by it after every
   * mixer update. See hipsLegScale.ts.
   *
   * ⚠ THIS REPLACED A CONSTANT ROOT LIFT of (his rest hips - the master's),
   * which was exact at rest and wrong in proportion to the clip's own vertical
   * travel — 58 mm of float on the keeper and 43 mm of burial on the winger,
   * on a clip whose hips drop 833 mm. A shift cannot correct a scale, and the
   * error hid at rest, which is where every still was taken.
   */
  readonly restHipsY: number;
}

/**
 * One player off a loaded tier.
 *
 * `SkeletonUtils.clone` copies the node graph and gives the copy its own
 * `Skeleton` while SHARING geometry, materials and textures with every other
 * instance OF THE SAME CHARACTER. For the cast that sharing only ever spans one
 * man — a second Brennan is free, a second player is not — which is the whole
 * cost model in one sentence.
 */
export function instantiateCast(id: string, lod: CastLodLevel): CastInstance | null {
  const tier = castLodLoaded(id, lod);
  if (!tier) return null;
  const root = cloneSkinned(tier.template) as Object3D;
  root.updateMatrixWorld(true);

  const meshes: SkinnedMesh[] = [];
  root.traverse((o) => {
    if ((o as SkinnedMesh).isSkinnedMesh) meshes.push(o as SkinnedMesh);
  });
  const skeleton = meshes[0]?.skeleton;
  if (!skeleton) return null;

  let drawCalls = 0;
  for (const m of meshes) {
    const groups = (m.geometry as BufferGeometry).groups?.length ?? 0;
    drawCalls += groups > 1 ? groups : 1;
  }

  return {
    id,
    lod,
    root,
    meshes,
    skeleton,
    bones: skeleton.bones,
    hands: findHandRig(skeleton.bones),
    triangles: tier.triangles,
    drawCalls,
    restHipsY: tier.hipsRestY,
  };
}

/**
 * Free ONE tier's GPU resources — and nothing else's.
 *
 * ⚠ The subtle half is what it refuses to free. Since the optimised cast reads
 * its images out of `cast-opt/_shared/`, a texture hanging off this tier's
 * material is very likely the SAME `Texture` object thirty-five other
 * characters are drawing with. Disposing it here does not error; it silently
 * black-holes everyone else's shirt the next frame, in a tier nobody touched.
 * Pooled textures belong to `castDecoders.ts` and are freed only by
 * `clearSharedTextureCache()`. Geometry and materials are genuinely per-tier
 * and are freed here as they always were.
 *
 * Exported because "does the shared texture survive one tier's eviction" is the
 * whole point and has to be provable without a GPU.
 */
export function disposeCastTemplate(template: Object3D): void {
  template.traverse((o) => {
    const mesh = o as SkinnedMesh;
    if (!mesh.isMesh) return;
    mesh.geometry?.dispose();
    for (const m of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
      if (!m) continue;
      for (const slot of TEXTURE_SLOTS) {
        const t = (m as unknown as Record<string, Texture | null>)[slot];
        if (t && !isSharedTexture(t)) t.dispose();
      }
      m.dispose();
    }
  });
}

/**
 * Drop everything. Tests and a benchmark sweeping tiers.
 *
 * Note what this does NOT do: clear the shared texture pool. Evicting the cast
 * and evicting the images the cast happened to reference are two decisions —
 * a tier sweep re-loads the same GLBs and wants the pool warm. Call
 * `clearSharedTextureCache()` explicitly when the pool itself must go.
 */
export function disposeCast(): void {
  for (const tier of loaded.values()) disposeCastTemplate(tier.template);
  loaded.clear();
  inFlight.clear();
  refusals.clear();
}

/** Characters currently resident, for the perf overlay and the benchmark. */
export function castResidency(): {
  tiers: number;
  triangles: number;
  drawCallsPerCopy: number;
  characters: number;
} {
  const chars = new Set<string>();
  let triangles = 0;
  let draws = 0;
  for (const t of loaded.values()) {
    chars.add(t.id);
    triangles += t.triangles;
    draws += t.materials;
  }
  return { tiers: loaded.size, triangles, drawCallsPerCopy: draws, characters: chars.size };
}

export type { CastCharacter };
